/**
 * The patient overview endpoint, as the frontend sees it (PHASE-0.md §2.4).
 *
 * Mirrors `api/src/Continuum.Api/Patients/PatientOverview.cs`. **One screen, one
 * request** — §2.4 asks for no tab structure and the response is shaped the same way,
 * for the reason that file gives: a screen assembled from four requests can render
 * three of them, and the one that failed is the one that was going to say
 * *penicillinallergi*.
 *
 * **Values and codes, never rendered text** (AGENTS.md §7, I18N.md §1). The birth date
 * is a calendar date, the dose is a number and its unit, the allergy severity and the
 * flag type are codes. Turning any of them into words is `patientOverviewDisplay.ts`,
 * which needs a catalogue to do it. There is no age here at all: it is a function of
 * today, and an age computed on the server freezes today at the moment of
 * serialisation.
 */

import { unauthenticated } from '../auth/sessionExpiry'
import type { AllergyStatus, IdentifierSystem } from './patientRecord'

/**
 * The flag types the schema permits today — `safety_flag_type_known`, migration 0005.
 *
 * **The list is closed in the database and open on screen**, which is why nothing here
 * narrows {@link PatientSafetyFlag.flagType} to this union. A type added by a later
 * migration reaches the screen as the code it is rather than being dropped by a
 * frontend that has not been taught it, and a dropped safety flag is the failure
 * nobody notices. `flagLabel` in `patientOverviewDisplay.ts` is where that fallback
 * lives; this constant is what it recognises.
 */
export const KNOWN_FLAG_TYPES = [
  'dnr',
  'treatment-limitation',
  'isolation',
  'infection-risk',
  'fall-risk',
  'allergy-status-unknown',
  'weight',
] as const

export type KnownFlagType = (typeof KNOWN_FLAG_TYPES)[number]

/** One identifier the patient holds. */
export interface PatientIdentifierView {
  readonly system: IdentifierSystem

  /** The identifier itself, exactly as recorded. Never reformatted. */
  readonly value: string

  /**
   * The unit that issued it, for a reservnummer. Null for a personnummer — Skatteverket
   * issues those nationally.
   *
   * Displayed, because a reservnummer is only unique within the block its owner
   * allocated: two units can hold the same number for two patients.
   */
  readonly assigner: string | null
}

/**
 * Where the patient is.
 *
 * Present whenever there is a current admission. {@link ward} may still be null — an
 * emergency presentation has no ward yet — and that is an admission with an unknown
 * ward rather than no admission, which is why {@link admittedAt} is not nullable.
 */
export interface PatientLocationView {
  readonly ward: string | null
  readonly wardName: string | null
  readonly bed: string | null
  readonly admittedAt: string
  readonly responsibleGivenName: string | null
  readonly responsibleFamilyName: string | null

  /**
   * The encounter's `record_id` — the vårdtillfälle across every move. What a transfer names in its
   * route (DECISIONS.md #48). Not displayed.
   */
  readonly encounterId: string

  /** The current encounter version's `version_id`. What a transfer sends back as `supersedes`. */
  readonly encounterVersionId: string

  /**
   * ISO 8601 instant: since when the patient has been where they are — the admission, or the last
   * move. A move may not be dated before it, and the transfer form says so.
   */
  readonly inPlaceSince: string
}

/** One allergy as the record holds it. */
export interface PatientAllergy {
  /**
   * The allergy's `record_id` — its stable logical identity across versions.
   *
   * Added by DECISIONS.md #46 point 9, along with the two below. Until a correction existed these
   * rows reached the screen with no identity on them at all, which is why the panel could only ever
   * display them.
   */
  readonly allergyId: string

  /** The `version_id` on screen. What a correction sends back as `supersedes`. Not displayed. */
  readonly versionId: string

  /** 1 for a row nobody has corrected, then 2, 3 … Displayed only above 1, like a note's. */
  readonly version: number

  /** `active`, `resolved` or `no-known-allergies`. */
  readonly status: string

  /**
   * The substance, as documented. Record content, so never translated (AGENTS.md §3
   * rule 8). Null only for `no-known-allergies`, which names none.
   */
  readonly substance: string | null

  readonly atcCode: string | null

  /** What happened, as documented. Record content. */
  readonly reaction: string | null

  /** `mild`, `moderate` or `severe`, or null — and null is not `mild`. */
  readonly severity: string | null

  /** `low`, `high` or `unable-to-assess`, or null. */
  readonly criticality: string | null

  /** `confirmed` or `unconfirmed`, or null. */
  readonly verification: string | null

  readonly effectiveAt: string
}

/** One active safety flag. */
export interface PatientSafetyFlag {
  /** The flag's `record_id` (#46 point 9). What a correction is addressed to. */
  readonly flagId: string

  /** The `version_id` on screen. What a correction sends back as `supersedes`. Not displayed. */
  readonly versionId: string

  /** 1 for a row nobody has corrected, then 2, 3 … Displayed only above 1. */
  readonly version: number

  /** A {@link KnownFlagType}, or a code a later migration added. Never narrowed here. */
  readonly flagType: string

  /**
   * The flag in brief: a weight in kilograms, an isolation type, or a treatment
   * limitation in a line.
   *
   * **Record content in the documenter's words — not parsed, not converted, not
   * reformatted.** A weight is a number in a text column and it stays text all the way
   * to the screen; the unit is the interface's to add (`patient:safety.weightKg`) and
   * the number is never the interface's to touch.
   */
  readonly value: string | null

  /** The reasoning, where {@link value} is the brief. Record content. */
  readonly note: string | null

  readonly decidedByGivenName: string | null
  readonly decidedByFamilyName: string | null

  /** The decider's documented professional title — `läkare`. Record content. */
  readonly decidedByRoleTitle: string | null

  readonly decidedAt: string | null
  readonly effectiveAt: string
}

/**
 * One current medication order.
 *
 * **Correctable since DECISIONS.md #46, but still never prescribed here**: the dosing and the
 * standing can be corrected, a first version cannot be written, and nothing about administration
 * moved.
 */
export interface PatientMedication {
  /** The order's `record_id`. Addressed by a correction, and the route it is posted to. */
  readonly medicationId: string

  /** The `version_id` on screen. What a correction sends back as `supersedes` (#46 point 9). */
  readonly versionId: string

  /** 1 for an order nobody has corrected, then 2, 3 … Displayed only above 1. */
  readonly version: number

  /** Substance name as ordered. Record content. */
  readonly substance: string

  /**
   * ATC code, and null for every order in the record today.
   *
   * Carried because the column exists, and deliberately unused: the only thing an
   * ATC-coded order enables is an interaction check, which is clinical decision support
   * (AGENTS.md §3 rule 5).
   */
  readonly atcCode: string | null

  /**
   * Strength as labelled on the package, e.g. `40 mg`. Verbatim, and **not** put
   * through the dose formatter — it is what is printed on the box, and rewriting it
   * would make the screen disagree with the packet in the clinician's hand.
   */
  readonly strength: string

  readonly doseForm: string

  /**
   * The dose as a number, rendered only through the shared dose formatter.
   *
   * `numeric` in Postgres and `decimal` in C#, so it arrives as a JSON number and is
   * typed as one — `JSON.parse` has already happened by the time any of our code sees
   * it, and pretending otherwise would be a lie about what is in the object.
   * `formatDose` accepts a number and refuses exponent notation, which a dose in mass
   * units cannot reach: `0.25` and `100` round-trip through a double exactly, and JS
   * prints the shortest representation that does.
   */
  readonly doseAmount: number

  readonly doseUnit: string
  readonly route: string

  /** How often, in the prescriber's words: `1 gång per dygn`. Record content. */
  readonly frequency: string

  /** Vid behov. */
  readonly isPrn: boolean

  /**
   * The 24-hour ceiling for a vid behov order, verbatim. Never computed from
   * {@link doseAmount} and {@link frequency} — that computation is dose calculation
   * (AGENTS.md §3 rule 5).
   */
  readonly prnMaxDose24h: string | null

  /** Indication — ordinationsorsak, mandatory under HSLF-FS 2017:37. Record content. */
  readonly indication: string

  readonly startAt: string
  readonly endAt: string | null
  readonly prescriberGivenName: string | null
  readonly prescriberFamilyName: string | null
}

/** One note, as the overview lists it. */
export interface PatientNote {
  readonly noteId: string

  /**
   * Which version is shown. Greater than 1 means the note has been amended, and the
   * earlier versions are still in the record.
   *
   * Displayed, because a corrected note that does not say it was corrected is the thing
   * an append-only record exists to prevent.
   */
  readonly version: number

  /** `signed` or `amended`. A draft never reaches here. */
  readonly status: string

  /** Local note type — `daily`, `admission`, `discharge`, … An open vocabulary. */
  readonly noteType: string

  /** BCP 47 language subtag the note was written in. Never a reason to translate it. */
  readonly language: string

  readonly subjective: string | null
  readonly objective: string | null
  readonly assessment: string | null
  readonly plan: string | null
  readonly bodyText: string | null

  /** When the note's content is clinically true. What the list is ordered by. */
  readonly effectiveAt: string

  /**
   * When the row was written.
   *
   * Shown alongside {@link effectiveAt} when they differ, which is what makes
   * retroactive documentation visible rather than silent (AGENTS.md §4).
   */
  readonly recordedAt: string

  readonly authorGivenName: string | null
  readonly authorFamilyName: string | null
  readonly authorRoleTitle: string | null

  /**
   * The note's active forensic-relevance marker, or null (DECISIONS.md #53 step 2). With
   * {@link forensicMarkerVersionId}, what a withdrawal names. Shown to everybody who reads the note.
   */
  readonly forensicMarkerId: string | null
  readonly forensicMarkerVersionId: string | null
}

/**
 * A recorded death — DECISIONS.md #53. Mirrors `PatientDeathView`.
 *
 * **Three times, and the screen shows two of them apart:** when the patient died, and when a
 * physician established it. Every instant goes through the shared formatter (AGENTS.md §5).
 */
export interface PatientDeathView {
  /** `clinical.death.record_id` — what a correction names in its route. */
  readonly deathId: string

  /** What a correction supersedes. Not displayed. */
  readonly versionId: string
  readonly version: number

  /** The time of death, ISO 8601. */
  readonly diedAt: string

  /** Whether {@link diedAt} is an estimate — the normal case for a patient found dead. */
  readonly timeOfDeathEstimated: boolean

  /** When the death was established, ISO 8601. */
  readonly pronouncedAt: string

  readonly pronouncedByGivenName: string | null
  readonly pronouncedByFamilyName: string | null

  /** As documented — `läkare`. Record content, never translated. */
  readonly pronouncedByRoleTitle: string | null

  /** `notified`, `not-notified` or `not-assessed`. */
  readonly policeNotification: string

  /** The circumstances, in the physician's words. Record content. */
  readonly note: string | null
}

/** One patient's record as the overview screen shows it. */
export interface PatientOverview {
  readonly patientId: string

  /**
   * The `version_id` of the identity this response carries.
   *
   * **Not displayed.** It is what a correction sends back as `supersedes` (DECISIONS.md #43), which
   * is how two clinicians correcting one unidentified patient cannot silently overwrite each other
   * — the second is refused rather than accepted.
   */
  readonly versionId: string

  /**
   * 1 for a patient nobody has corrected, then 2, 3 …
   *
   * **Displayed only when it is greater than 1**, and it is the way into the identity history
   * (`DECISIONS.md` #45). It went undisplayed for the length of one afternoon, when there was no
   * `GET` on `/versions` and a marker would have announced a history nobody could open; now
   * `patientVersionsApi.ts` reads that endpoint and the identity section carries a toggle beside
   * this number, the same pattern as a note's. Worded *Visa alla versioner av uppgifterna* rather
   * than the note's *Visa alla versioner*, because a screen showing three notes already has three
   * buttons by that name.
   *
   * Version 1 stays silent on purpose. "Version 1" on the great majority of patients is noise that
   * teaches a reader to skip the line, and the line is the only thing distinguishing the patient
   * whose identity *has* moved.
   */
  readonly version: number

  readonly givenName: string | null
  readonly familyName: string | null

  /** `YYYY-MM-DD`, or null when the identity is not established. */
  readonly birthDate: string | null

  /**
   * HL7 administrative gender — `male`, `female`, `other`, `unknown` — or null.
   *
   * `unknown` is a recorded value and not the same as null: somebody looked and could
   * not say.
   */
  readonly sex: string | null

  readonly isEstimatedIdentity: boolean

  /** Skyddad identitet. */
  readonly confidentialityMarked: boolean

  /**
   * Every active identifier, most nationally recognised first.
   *
   * **All of them**, unlike the ward list and the search, which have one column each: a
   * patient who arrived unidentified and later acquired a personnummer holds both, and
   * the reservnummer is what the wristband, the labels and yesterday's notes say.
   */
  readonly identifiers: readonly PatientIdentifierView[]

  readonly location: PatientLocationView | null

  /**
   * The patient's recorded death, or null (DECISIONS.md #53).
   *
   * A death and a {@link location} together are the normal case, not a contradiction: recording a
   * death does not end the stay, because the body is often still on the ward.
   */
  readonly death: PatientDeathView | null

  /** The badge's answer, resolved by the same precedence the two lists use. */
  readonly allergyStatus: AllergyStatus

  /**
   * The allergy rows themselves, severe first.
   *
   * Both this and {@link allergyStatus} are needed: a patient can carry a documented
   * allergy *and* an unknown-status flag, and the badge has to say allergy while the
   * panel still shows that the list is not known to be complete.
   */
  readonly allergies: readonly PatientAllergy[]

  readonly safetyFlags: readonly PatientSafetyFlag[]
  readonly medications: readonly PatientMedication[]
  readonly notes: readonly PatientNote[]

  /**
   * There are older notes than the ones in {@link notes}.
   *
   * Said out loud by the server rather than inferred from a count, for the same reason
   * the search says `truncated`: a clinician who cannot see that a list is cut short
   * reads the oldest visible note as the first one there is.
   */
  readonly moreNotes: boolean
}

/**
 * Why an overview could not be shown. Codes, so the catalogue does the words.
 *
 * `not-found` covers a patient who is not in the record *and* one retracted as
 * `entered-in-error` — the server answers the same for both, deliberately, because the
 * difference is not one to disclose to somebody who guessed a uuid.
 */
export type PatientOverviewFailure = 'not-found' | 'unavailable'

export class PatientOverviewError extends Error {
  readonly failure: PatientOverviewFailure

  constructor(failure: PatientOverviewFailure) {
    super(`Patient overview failed: ${failure}`)
    this.name = 'PatientOverviewError'
    this.failure = failure
  }
}

/**
 * Reads one patient's overview.
 *
 * **Every call writes an audit row that can never be deleted** (AGENTS.md §3, §4). The
 * row is written from the route before the handler runs, so it names the patient even
 * when the read then failed — which is why this is called once per patient a clinician
 * opens, and not on a hover, a prefetch or a poll. There is no refresh timer on the
 * overview for that reason.
 */
export async function fetchPatientOverview(
  patientId: string,
  signal: AbortSignal,
): Promise<PatientOverview> {
  let response: Response

  try {
    response = await fetch(`/api/patients/${encodeURIComponent(patientId)}`, {
      signal,
      headers: { Accept: 'application/json' },
    })
  } catch (cause) {
    // An aborted fetch is the clinician having moved on, not a failure. Re-thrown so
    // the caller can tell the two apart rather than showing an error for a screen
    // nobody is looking at.
    if (signal.aborted) {
      throw cause
    }
    throw new PatientOverviewError('unavailable')
  }

  if (response.status === 404) {
    throw new PatientOverviewError('not-found')
  }

  // Before the general case, because a 401 is not an unavailable box: it means the session
  // has ended, and `SessionGate` is about to show the login screen. Reported rather than
  // rendered — see `auth/sessionExpiry.ts`. The throw still happens, so this screen does not
  // sit half-updated in the moment before it unmounts.
  unauthenticated(response)

  if (!response.ok) {
    throw new PatientOverviewError('unavailable')
  }

  return (await response.json()) as PatientOverview
}
